import React from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { useAuth } from "../../auth/AuthProvider";
import airtable from "../../lib/airtables";
import { teacherFieldIds } from "../../lib/airtables/tables/Teachers";
import { bookingFieldIds, bookingFieldNames } from "../../lib/airtables/tables/Bookings";
import { responseFieldIds, responseFieldNames } from "../../lib/airtables/tables/Responses";
import "./styles.scss";

type AvailabilitySlot = {
  date: string;
  startTime: string;
  notes: string;
};

type BookingStatus = string;

type HistoricalDateOption = {
  dateTime: string;
  notes: string;
};

const BOOKING_STATUSES = {
  awaitingEducatorProposal: "Awaiting Educator Proposal",
  awaitingArtistDecision: "Awaiting Artist Decision",
  awaitingArtistProposal: "Awaiting Artist Proposal",
  awaitingEducatorDecision: "Awaiting Educator Decision",
  dateSelected: "Date Selected"
} as const;

const normalizeBookingStatus = (status: string) => status.trim().toLowerCase();

const initialSlots: AvailabilitySlot[] = [
  { date: "", startTime: "", notes: "" },
  { date: "", startTime: "", notes: "" },
  { date: "", startTime: "", notes: "" }
];

const getTodayIsoDate = () => {
  const now = new Date();
  const local = new Date(now.getTime() - now.getTimezoneOffset() * 60000);
  return local.toISOString().slice(0, 10);
};

const formatFriendlyDate = (isoDate: string) => {
  if (!isoDate) return "Select a date";

  const parsed = new Date(`${isoDate}T00:00:00`);
  if (Number.isNaN(parsed.getTime())) return "Select a date";

  const dayOfWeek = new Intl.DateTimeFormat("en-CA", { weekday: "long" }).format(parsed);
  const month = new Intl.DateTimeFormat("en-CA", { month: "long" }).format(parsed);
  const day = String(parsed.getDate()).padStart(2, "0");
  const year = parsed.getFullYear();

  return `${dayOfWeek}, ${month}, ${day}, ${year}`;
};

const formatFriendlyTime = (timeValue: string) => {
  if (!timeValue) return "Select start time";

  const [hourString, minuteString] = timeValue.split(":");
  const hour = Number(hourString);
  const minute = Number(minuteString);

  if (Number.isNaN(hour) || Number.isNaN(minute)) return "Select start time";

  const meridiem = hour >= 12 ? "PM" : "AM";
  const hour12 = hour % 12 || 12;

  return `${hour12}:${String(minute).padStart(2, "0")} ${meridiem}`;
};

const toBookingDateTime = (date: string, startTime: string) => {
  if (!date || !startTime) return "";

  const isoDateTime = new Date(`${date}T${startTime}:00`);
  if (Number.isNaN(isoDateTime.getTime())) return "";

  return isoDateTime.toISOString();
};

const getStringValue = (value: any) => {
  if (Array.isArray(value)) return value[0] || "";
  if (typeof value === "string") return value;
  return "";
};

const getStatusValue = (value: any): BookingStatus => {
  if (!value) return "";
  if (typeof value === "string") return value as BookingStatus;

  if (Array.isArray(value)) {
    const firstValue = value[0];
    if (typeof firstValue === "string") return firstValue as BookingStatus;
    if (firstValue && typeof firstValue === "object" && typeof firstValue.name === "string") {
      return firstValue.name as BookingStatus;
    }
  }

  if (typeof value === "object" && typeof value.name === "string") {
    return value.name as BookingStatus;
  }

  return "";
};

const isEducatorEditableStatus = (status: BookingStatus) => {
  const normalizedStatus = normalizeBookingStatus(status);
  return [
    "educator responding",
    "respond again educator",
    normalizeBookingStatus(BOOKING_STATUSES.awaitingEducatorProposal)
  ].includes(normalizedStatus);
};

const isEducatorDecisionStatus = (status: BookingStatus) => {
  const normalizedStatus = normalizeBookingStatus(status);
  return normalizedStatus === normalizeBookingStatus(BOOKING_STATUSES.awaitingEducatorDecision);
};

const isEducatorSubmittedStatus = (status: BookingStatus) => {
  const normalizedStatus = normalizeBookingStatus(status);
  return [
    "artist responding",
    "respond again artist",
    normalizeBookingStatus(BOOKING_STATUSES.awaitingArtistDecision),
    normalizeBookingStatus(BOOKING_STATUSES.awaitingArtistProposal),
    normalizeBookingStatus(BOOKING_STATUSES.dateSelected)
  ].includes(normalizedStatus);
};

const isArtistProposalStatus = (status: BookingStatus) => {
  const normalizedStatus = normalizeBookingStatus(status);
  return [
    "respond again artist",
    normalizeBookingStatus(BOOKING_STATUSES.awaitingArtistProposal)
  ].includes(normalizedStatus);
};

const isArtistDecisionStatus = (status: BookingStatus) => {
  const normalizedStatus = normalizeBookingStatus(status);
  return [
    "artist responding",
    normalizeBookingStatus(BOOKING_STATUSES.awaitingArtistDecision)
  ].includes(normalizedStatus);
};

const getDateAndTimeFromBookingValue = (value: any) => {
  const isoValue = getStringValue(value);
  if (!isoValue) {
    return { date: "", startTime: "" };
  }

  const parsed = new Date(isoValue);
  if (Number.isNaN(parsed.getTime())) {
    return { date: "", startTime: "" };
  }

  const date = `${parsed.getFullYear()}-${String(parsed.getMonth() + 1).padStart(2, "0")}-${String(parsed.getDate()).padStart(2, "0")}`;
  const startTime = `${String(parsed.getHours()).padStart(2, "0")}:${String(parsed.getMinutes()).padStart(2, "0")}`;

  return { date, startTime };
};

const getBookingValue = (booking: any, fieldId: string, fieldName: string) => booking?.[fieldId] ?? booking?.[fieldName];

const getResponseValue = (response: any, fieldId: string, fieldName: string) => response?.[fieldId] ?? response?.[fieldName];

const getResponseActionValue = (response: any) => {
  const actionValue = response?.Action ?? response?.action ?? response?.["Response Action"] ?? response?.["Response action"];
  if (Array.isArray(actionValue)) return String(actionValue[0] || "").trim().toLowerCase();
  if (typeof actionValue === "string") return actionValue.trim().toLowerCase();
  return "";
};

const getHistoricalDateParts = (isoDateTime: string) => {
  const parsed = new Date(isoDateTime);
  if (Number.isNaN(parsed.getTime())) {
    return {
      yearKey: "Unknown Year",
      monthKey: "Unknown Month",
      dayKey: "Unknown Day",
      dayLabel: "Unknown Day"
    };
  }

  const dayNumber = String(parsed.getDate()).padStart(2, "0");
  const weekday = new Intl.DateTimeFormat("en-CA", { weekday: "long" }).format(parsed);

  return {
    yearKey: String(parsed.getFullYear()),
    monthKey: new Intl.DateTimeFormat("en-CA", { month: "long" }).format(parsed),
    dayKey: `${dayNumber}-${weekday}`,
    dayLabel: `${dayNumber} - ${weekday}`
  };
};

const formatHistoricalTime = (isoDateTime: string) => {
  const parsed = new Date(isoDateTime);
  if (Number.isNaN(parsed.getTime())) return "Unknown time";

  return new Intl.DateTimeFormat("en-CA", {
    hour: "numeric",
    minute: "2-digit"
  }).format(parsed);
};


const GATE_ENDPOINT = process.env.REACT_APP_BOOKING_GATE_ENDPOINT || "/api/artist-ambassador-scheduling-gate";

const getBookingFromGateApi = async (email: string) => {
  try {
    const response = await fetch(GATE_ENDPOINT, {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ email })
    });

    if (!response.ok) return null;

    const body = await response.json();
    if (!body?.booking || !body?.booking?.id) return null;

    return body.booking;
  } catch {
    return null;
  }
};

const ScheduleSection = ({
  title,
  subtitle,
  children,
  className = ""
}: {
  title: string;
  subtitle?: string;
  children: React.ReactNode;
  className?: string;
}) => (
  <section className={`artist-ambassador-panel ${className}`.trim()}>
    <div className="artist-ambassador-panel__header">
      <h2 className="artist-ambassador-section-title">{title}</h2>
      {subtitle ? <p className="artist-ambassador-section-copy">{subtitle}</p> : null}
    </div>
    {children}
  </section>
);

const SlotSummaryCard = ({
  slot,
  index,
  isConfirmed,
  actionLabel,
  onAction,
  isActionDisabled,
  isActionSaving
}: {
  slot: AvailabilitySlot;
  index: number;
  isConfirmed?: boolean;
  actionLabel?: string;
  onAction?: () => void;
  isActionDisabled?: boolean;
  isActionSaving?: boolean;
}) => (
  <article className={`artist-ambassador-summary-card ${isConfirmed ? "artist-ambassador-summary-card--confirmed" : ""}`.trim()}>
    <div className="artist-ambassador-summary-card__content">
      <p className="artist-ambassador-summary-card__label">Date {index + 1}</p>
      <p className="artist-ambassador-summary-card__datetime">{formatFriendlyDate(slot.date)}</p>
      <p className="artist-ambassador-summary-card__datetime">{formatFriendlyTime(slot.startTime)}</p>
      {slot.notes?.trim() ? <p className="artist-ambassador-summary-card__notes">{slot.notes}</p> : null}
      {isConfirmed ? <p className="artist-ambassador-summary-card__confirmed">Confirmed with educator</p> : null}
    </div>

    {actionLabel && onAction ? (
      <button
        type="button"
        className="artist-ambassador-button artist-ambassador-button--tile"
        onClick={onAction}
        disabled={isActionDisabled}
      >
        {isActionSaving ? "Saving..." : actionLabel}
      </button>
    ) : null}
  </article>
);

export default function ArtistAmbassadorSchedulingPage() {
  const navigate = useNavigate();
  const location = useLocation();
  const { user } = useAuth();
  const [slots, setSlots] = React.useState<AvailabilitySlot[]>(initialSlots);
  const [confirmedSlots, setConfirmedSlots] = React.useState<boolean[]>(
    initialSlots.map(() => false)
  );
  const [openSlotIndex, setOpenSlotIndex] = React.useState(0);
  const [statusMessage, setStatusMessage] = React.useState("");
  const [isLoadingBooking, setIsLoadingBooking] = React.useState(true);
  const [isAllowedEducator, setIsAllowedEducator] = React.useState(false);
  const [isSaving, setIsSaving] = React.useState(false);
  const [bookingRecordId, setBookingRecordId] = React.useState("");
  const [bookingStatus, setBookingStatus] = React.useState<BookingStatus>("");
  const [artistActionMessage, setArtistActionMessage] = React.useState("");
  const [selectedArtistDateIndex, setSelectedArtistDateIndex] = React.useState<number | null>(null);
  const [confirmedArtistDateIndex, setConfirmedArtistDateIndex] = React.useState<number | null>(null);
  const [showArtistProposalTool, setShowArtistProposalTool] = React.useState(false);
  const [showEducatorProposalTool, setShowEducatorProposalTool] = React.useState(false);
  const [historicalDateOptions, setHistoricalDateOptions] = React.useState<HistoricalDateOption[]>([]);
  const [bookingRoundNumber, setBookingRoundNumber] = React.useState(0);
  const minSelectableDate = React.useMemo(() => getTodayIsoDate(), []);
  const dateInputRefs = React.useRef<Array<HTMLInputElement | null>>([]);
  const timeInputRefs = React.useRef<Array<HTMLInputElement | null>>([]);

  const userName = user?.[teacherFieldIds.name] ?? "Educator";
  const userEmail = user?.[teacherFieldIds.email] ?? "";
  const isArtistUser = user?.[teacherFieldIds.artist] === "Yes";
  const groupedHistoricalDateOptions = React.useMemo(() => {
    const grouped = historicalDateOptions.reduce((result, option) => {
      const { yearKey, monthKey, dayKey, dayLabel } = getHistoricalDateParts(option.dateTime);

      if (!result[yearKey]) {
        result[yearKey] = {};
      }

      if (!result[yearKey][monthKey]) {
        result[yearKey][monthKey] = {};
      }

      if (!result[yearKey][monthKey][dayKey]) {
        result[yearKey][monthKey][dayKey] = {
          dayLabel,
          options: [] as HistoricalDateOption[]
        };
      }

      result[yearKey][monthKey][dayKey].options.push(option);
      return result;
    }, {} as Record<string, Record<string, Record<string, { dayLabel: string; options: HistoricalDateOption[] }>>>);

    return Object.entries(grouped)
      .sort((a, b) => Number(b[0]) - Number(a[0]))
      .map(([year, months]) => ({
        year,
        months: Object.entries(months)
          .sort((a, b) => new Date(`${a[0]} 1, ${year}`).getTime() - new Date(`${b[0]} 1, ${year}`).getTime())
          .map(([month, days]) => ({
            month,
            dayGroups: Object.values(days)
              .map(dayGroup => ({
                ...dayGroup,
                options: [...dayGroup.options].sort((a, b) => new Date(a.dateTime).getTime() - new Date(b.dateTime).getTime())
              }))
              .sort((a, b) => new Date(a.options[0]?.dateTime || 0).getTime() - new Date(b.options[0]?.dateTime || 0).getTime())
          }))
      }));
  }, [historicalDateOptions]);

  const loadHistoricalDates = React.useCallback(async (bookingId: string) => {
    if (!bookingId) {
      setHistoricalDateOptions([]);
      return;
    }

    try {
      const responses = await airtable.responses.getByBookingIdFromName(bookingId);
      const parsed = responses.flatMap(response => {
        const action = getResponseActionValue(response);
        if (action && !action.includes("propos")) return [];
        const values: HistoricalDateOption[] = [
          {
            dateTime: getStringValue(getResponseValue(response, responseFieldIds.date1, responseFieldNames.date1)),
            notes: getStringValue(getResponseValue(response, responseFieldIds.date1Notes, responseFieldNames.date1Notes))
          },
          {
            dateTime: getStringValue(getResponseValue(response, responseFieldIds.date2, responseFieldNames.date2)),
            notes: getStringValue(getResponseValue(response, responseFieldIds.date2Notes, responseFieldNames.date2Notes))
          },
          {
            dateTime: getStringValue(getResponseValue(response, responseFieldIds.date3, responseFieldNames.date3)),
            notes: getStringValue(getResponseValue(response, responseFieldIds.date3Notes, responseFieldNames.date3Notes))
          },
          {
            dateTime: getStringValue(getResponseValue(response, responseFieldIds.proposedDate4, responseFieldNames.proposedDate4)),
            notes: getStringValue(getResponseValue(response, responseFieldIds.date4Notes, responseFieldNames.date4Notes))
          },
          {
            dateTime: getStringValue(getResponseValue(response, responseFieldIds.alternateProposedTime, responseFieldNames.alternateProposedTime)),
            notes: getStringValue(getResponseValue(response, responseFieldIds.notes, responseFieldNames.notes))
          }
        ];

        return values.filter(value => Boolean(value.dateTime));
      });

      const deduped = parsed.filter((item, idx, list) => list.findIndex(candidate => candidate.dateTime === item.dateTime && candidate.notes === item.notes) === idx);
      deduped.sort((a, b) => new Date(a.dateTime).getTime() - new Date(b.dateTime).getTime());
      setHistoricalDateOptions(deduped);
    } catch (error) {
      console.error("Failed to load historical date options", error);
      setHistoricalDateOptions([]);
    }
  }, []);

  React.useEffect(() => {
    const loadBooking = async () => {
      if (!userEmail) {
        setIsLoadingBooking(false);
        setIsAllowedEducator(false);
        return;
      }

      try {
        const bookingFromGate = isArtistUser ? null : await getBookingFromGateApi(userEmail);
        const booking = bookingFromGate || (isArtistUser
          ? await airtable.bookings.getBookingByArtistEmail(userEmail)
          : await airtable.bookings.getBookingByEducatorEmail(userEmail));

        if (!booking?.id) {
          setIsAllowedEducator(false);
          setIsLoadingBooking(false);
          return;
        }

        setBookingRecordId(booking.id);
        setIsAllowedEducator(true);

        const fetchedBookingId = getStringValue(getBookingValue(booking, bookingFieldIds.bookingId, bookingFieldNames.bookingId));
        await loadHistoricalDates(fetchedBookingId);

        const currentStatus = getStatusValue(getBookingValue(booking, bookingFieldIds.status, bookingFieldNames.status));
        setBookingStatus(currentStatus);
        setShowArtistProposalTool(isArtistUser && isArtistProposalStatus(currentStatus));
        setShowEducatorProposalTool(!isArtistUser && isEducatorEditableStatus(currentStatus));

        const parsedRoundNumberRaw = getBookingValue(booking, bookingFieldIds.roundNumber, bookingFieldNames.roundNumber);
        const parsedRoundNumber = Number(Array.isArray(parsedRoundNumberRaw) ? parsedRoundNumberRaw[0] : parsedRoundNumberRaw);
        setBookingRoundNumber(Number.isFinite(parsedRoundNumber) && parsedRoundNumber > 0 ? parsedRoundNumber : 0);

        const proposedSlotsFromBooking = [
          {
            ...getDateAndTimeFromBookingValue(getBookingValue(booking, bookingFieldIds.proposedDate1, bookingFieldNames.proposedDate1)),
            notes: getStringValue(getBookingValue(booking, bookingFieldIds.date1Notes, bookingFieldNames.date1Notes))
          },
          {
            ...getDateAndTimeFromBookingValue(getBookingValue(booking, bookingFieldIds.proposedDate2, bookingFieldNames.proposedDate2)),
            notes: getStringValue(getBookingValue(booking, bookingFieldIds.date2Notes, bookingFieldNames.date2Notes))
          },
          {
            ...getDateAndTimeFromBookingValue(getBookingValue(booking, bookingFieldIds.proposedDate3, bookingFieldNames.proposedDate3)),
            notes: getStringValue(getBookingValue(booking, bookingFieldIds.date3Notes, bookingFieldNames.date3Notes))
          }
        ];

        const shouldStartWithBlankProposal = (isArtistUser && isArtistProposalStatus(currentStatus)) || (!isArtistUser && isEducatorEditableStatus(currentStatus));
        const parsedSlots = shouldStartWithBlankProposal
          ? initialSlots.map(slot => ({ ...slot }))
          : proposedSlotsFromBooking;

        setSlots(parsedSlots);

        const parsedConfirmed = parsedSlots.map(slot => Boolean(slot.date && slot.startTime));
        setConfirmedSlots(parsedConfirmed);

        if (parsedConfirmed.every(Boolean)) {
          setOpenSlotIndex(-1);
        } else {
          setOpenSlotIndex(parsedConfirmed.findIndex(isConfirmed => !isConfirmed));
        }

        const confirmedDateValue = getStringValue(getBookingValue(booking, bookingFieldIds.confirmedDateTime, bookingFieldNames.confirmedDateTime));
        const confirmedDateTimestamp = confirmedDateValue ? new Date(confirmedDateValue).getTime() : NaN;

        if (!Number.isNaN(confirmedDateTimestamp)) {
          const matchedIndex = parsedSlots.findIndex(slot => {
            const slotDateTime = toBookingDateTime(slot.date, slot.startTime);
            if (!slotDateTime) return false;
            const slotTimestamp = new Date(slotDateTime).getTime();
            return Math.abs(slotTimestamp - confirmedDateTimestamp) < 60000;
          });

          setConfirmedArtistDateIndex(matchedIndex >= 0 ? matchedIndex : null);
        } else {
          setConfirmedArtistDateIndex(null);
        }
      } catch (error) {
        console.error("Failed to load booking", error);
        setIsAllowedEducator(false);
        setStatusMessage("Unable to load your booking record right now. Please try again.");
      } finally {
        setIsLoadingBooking(false);
      }
    };

    loadBooking();
  }, [isArtistUser, loadHistoricalDates, userEmail]);

  React.useEffect(() => {
    if (isLoadingBooking || !isAllowedEducator) return;

    const basePath = "/app/artist-ambassador-scheduling";
    const path = location.pathname.replace(/\/+$/, "");
    const submittedPath = `${basePath}/submitted`;
    const formPath = `${basePath}/form`;
    const artistReviewPath = `${basePath}/artist-review`;

    if (isArtistUser) {
      const preferredArtistPath = showArtistProposalTool
        ? formPath
        : isArtistDecisionStatus(bookingStatus)
          ? artistReviewPath
          : submittedPath;

      if (path !== preferredArtistPath) {
        navigate(preferredArtistPath, { replace: true });
      }
      return;
    }

    const shouldShowSubmitted = isEducatorSubmittedStatus(bookingStatus) || isEducatorDecisionStatus(bookingStatus);

    if (showEducatorProposalTool) {
      if (path !== formPath) {
        navigate(formPath, { replace: true });
      }
      return;
    }

    if (shouldShowSubmitted && path !== submittedPath) {
      navigate(submittedPath, { replace: true });
      return;
    }

    if (!shouldShowSubmitted && path !== formPath && isEducatorEditableStatus(bookingStatus)) {
      navigate(formPath, { replace: true });
    }
  }, [bookingStatus, isAllowedEducator, isArtistUser, isLoadingBooking, location.pathname, navigate, showArtistProposalTool, showEducatorProposalTool]);

  const updateSlot = (
    slotIndex: number,
    field: keyof AvailabilitySlot,
    value: string
  ) => {
    setSlots(previousSlots =>
      previousSlots.map((slot, index) =>
        index === slotIndex ? { ...slot, [field]: value } : slot
      )
    );

    if (field === "date" || field === "startTime") {
      setConfirmedSlots(previous =>
        previous.map((isConfirmed, index) => (index >= slotIndex ? false : isConfirmed))
      );
      setOpenSlotIndex(slotIndex);
    }
  };

  const handleSubmit = async (event: React.FormEvent<HTMLFormElement>) => {
    event.preventDefault();

    if (!confirmedSlots.every(Boolean)) {
      setStatusMessage("Please confirm all three dates before final submission.");
      return;
    }

    if (!bookingRecordId) {
      setStatusMessage("No booking record was found for your account.");
      return;
    }

    setIsSaving(true);
    setStatusMessage("");

    const payload = {
      [bookingFieldIds.proposedDate1]: toBookingDateTime(slots[0].date, slots[0].startTime),
      [bookingFieldIds.proposedDate2]: toBookingDateTime(slots[1].date, slots[1].startTime),
      [bookingFieldIds.proposedDate3]: toBookingDateTime(slots[2].date, slots[2].startTime),
      [bookingFieldIds.date1Notes]: slots[0].notes,
      [bookingFieldIds.date2Notes]: slots[1].notes,
      [bookingFieldIds.date3Notes]: slots[2].notes,
      [bookingFieldIds.status]: isArtistUser && showArtistProposalTool ? BOOKING_STATUSES.awaitingEducatorDecision : BOOKING_STATUSES.awaitingArtistDecision,
      [bookingFieldIds.roundNumber]: bookingRoundNumber + 1
    };

    try {
      await airtable.bookings.update(bookingRecordId, payload);
      setBookingStatus(isArtistUser && showArtistProposalTool ? BOOKING_STATUSES.awaitingEducatorDecision : BOOKING_STATUSES.awaitingArtistDecision);
      setBookingRoundNumber(previous => previous + 1);
      if (isArtistUser) setShowArtistProposalTool(false);
      if (!isArtistUser) setShowEducatorProposalTool(false);
      navigate("/app/artist-ambassador-scheduling/submitted", { replace: true });
    } catch (error) {
      console.error("Failed to update booking", error);
      setStatusMessage("Unable to save your dates right now. Please try again.");
    } finally {
      setIsSaving(false);
    }
  };

  const openNativePicker = (input: HTMLInputElement | null) => {
    if (!input) return;

    input.focus();
    if (typeof input.showPicker === "function") {
      input.showPicker();
    }
  };

  const canAccessSlot = (slotIndex: number) => slotIndex === 0 || confirmedSlots[slotIndex - 1];

  const confirmSlot = (slotIndex: number) => {
    const currentSlot = slots[slotIndex];
    if (!currentSlot.date || !currentSlot.startTime) {
      setStatusMessage(`Please choose a date and time for Date ${slotIndex + 1}.`);
      return;
    }

    setConfirmedSlots(previous => previous.map((isConfirmed, index) => (index === slotIndex ? true : isConfirmed)));
    setStatusMessage("");

    if (slotIndex < slots.length - 1) {
      setOpenSlotIndex(slotIndex + 1);
      return;
    }

    setOpenSlotIndex(-1);
  };

  const openSlot = (slotIndex: number) => {
    if (!canAccessSlot(slotIndex)) return;
    setOpenSlotIndex(slotIndex);
  };

  const handleArtistSelectDate = async (slotIndex: number) => {
    const slot = slots[slotIndex];
    const confirmedDateTime = toBookingDateTime(slot.date, slot.startTime);

    if (!bookingRecordId) {
      setArtistActionMessage("No booking record was found for your account.");
      return;
    }

    if (!confirmedDateTime) {
      setArtistActionMessage("This date option is incomplete and cannot be selected.");
      return;
    }

    const isConfirmed = window.confirm("Are you sure?\nThis date will be confirmed and sent to the educator.");
    if (!isConfirmed) return;

    setSelectedArtistDateIndex(slotIndex);
    setArtistActionMessage("");

    try {
      await airtable.bookings.update(bookingRecordId, {
        [bookingFieldIds.confirmedDateTime]: confirmedDateTime,
        [bookingFieldIds.status]: BOOKING_STATUSES.dateSelected
      });
      setBookingStatus(BOOKING_STATUSES.dateSelected);
      setConfirmedArtistDateIndex(slotIndex);
      setArtistActionMessage(`Date ${slotIndex + 1} has been selected and sent to the educator.`);
    } catch (error) {
      console.error("Failed to save confirmed date", error);
      setArtistActionMessage("Unable to save your selected date right now. Please try again.");
    } finally {
      setSelectedArtistDateIndex(null);
    }
  };

  const handleArtistNoneOfTheseDatesWork = async () => {
    if (!bookingRecordId) {
      setArtistActionMessage("No booking record was found for your account.");
      return;
    }

    const shouldProceed = window.confirm("Are you sure?\nYou will be asked to suggest three alternative dates to the educator");
    if (!shouldProceed) return;

    setSelectedArtistDateIndex(-1);
    setArtistActionMessage("");

    try {
      await airtable.bookings.update(bookingRecordId, {
        [bookingFieldIds.status]: BOOKING_STATUSES.awaitingArtistProposal
      });

      setSlots(initialSlots);
      setConfirmedSlots(initialSlots.map(() => false));
      setOpenSlotIndex(0);
      setConfirmedArtistDateIndex(null);
      setBookingStatus(BOOKING_STATUSES.awaitingArtistProposal);
      setShowArtistProposalTool(true);
      setArtistActionMessage("Please propose three alternative dates for the educator.");
    } catch (error) {
      console.error("Failed to request alternate dates", error);
      setArtistActionMessage("Unable to send the alternate-date request right now. Please try again.");
    } finally {
      setSelectedArtistDateIndex(null);
    }
  };


  const handleEducatorSelectDate = async (slotIndex: number) => {
    const slot = slots[slotIndex];
    const confirmedDateTime = toBookingDateTime(slot.date, slot.startTime);

    if (!bookingRecordId) {
      setStatusMessage("No booking record was found for your account.");
      return;
    }

    if (!confirmedDateTime) {
      setStatusMessage("This date option is incomplete and cannot be selected.");
      return;
    }

    const isConfirmed = window.confirm("Are you sure?\nThis date will be confirmed and sent to the artist.");
    if (!isConfirmed) return;

    setSelectedArtistDateIndex(slotIndex);
    setStatusMessage("");

    try {
      await airtable.bookings.update(bookingRecordId, {
        [bookingFieldIds.confirmedDateTime]: confirmedDateTime,
        [bookingFieldIds.status]: BOOKING_STATUSES.dateSelected
      });
      setBookingStatus(BOOKING_STATUSES.dateSelected);
      setConfirmedArtistDateIndex(slotIndex);
      setStatusMessage(`Date ${slotIndex + 1} has been selected.`);
    } catch (error) {
      console.error("Failed to save selected date", error);
      setStatusMessage("Unable to save your selected date right now. Please try again.");
    } finally {
      setSelectedArtistDateIndex(null);
    }
  };

  const handleEducatorNoneOfTheseDatesWork = async () => {
    if (!bookingRecordId) {
      setStatusMessage("No booking record was found for your account.");
      return;
    }

    const shouldProceed = window.confirm("Are you sure?\nYou will be asked to suggest three alternative dates to the artist");
    if (!shouldProceed) return;

    setSelectedArtistDateIndex(-1);
    setStatusMessage("");

    try {
      await airtable.bookings.update(bookingRecordId, {
        [bookingFieldIds.status]: BOOKING_STATUSES.awaitingEducatorProposal
      });

      setSlots(initialSlots);
      setConfirmedSlots(initialSlots.map(() => false));
      setOpenSlotIndex(0);
      setConfirmedArtistDateIndex(null);
      setBookingStatus(BOOKING_STATUSES.awaitingEducatorProposal);
      setShowEducatorProposalTool(true);
      setStatusMessage("Please propose three alternative dates for the artist.");
    } catch (error) {
      console.error("Failed to request alternate dates", error);
      setStatusMessage("Unable to start a new proposal right now. Please try again.");
    } finally {
      setSelectedArtistDateIndex(null);
    }
  };

  if (isLoadingBooking) {
    return (
      <main className="page artist-ambassador-page">
        <div className="artist-ambassador-shell">
          <section className="artist-ambassador-panel artist-ambassador-panel--centered">
            <h1 className="artist-ambassador-title">Artist Ambassador Scheduling</h1>
            <p className="artist-ambassador-subtitle">Loading your booking details...</p>
          </section>
        </div>
      </main>
    );
  }

  if (!isAllowedEducator) {
    return (
      <main className="page artist-ambassador-page">
        <div className="artist-ambassador-shell">
          <section className="artist-ambassador-panel artist-ambassador-panel--centered">
            <h1 className="artist-ambassador-title">Artist Ambassador Scheduling</h1>
            <p className="artist-ambassador-subtitle">
              This page is only available to users with an existing booking record.
            </p>
          </section>
        </div>
      </main>
    );
  }

  const renderDateProposalTool = (forArtist: boolean) => (
    <main className="page artist-ambassador-page">
      <div className="artist-ambassador-shell">
        <section className="artist-ambassador-hero">
          <h1 className="artist-ambassador-title">Artist Ambassador Scheduling</h1>
          <p className="artist-ambassador-subtitle">
            {forArtist
              ? "Please propose three alternative dates for the educator."
              : `Welcome, ${userName}. Share three available date options for your classroom visit.`}
          </p>
          <p className="artist-ambassador-section-copy my-0">Each visit is typically one hour. Complete all three dates, then submit.</p>
        </section>

        <div className="artist-ambassador-proposal-layout">
          <form onSubmit={handleSubmit} className="artist-ambassador-form">
          {slots.map((slot, index) => (
            <section key={`slot-${index}`}>
              {openSlotIndex === index && canAccessSlot(index) ? (
                <div className="artist-ambassador-slot-card">
                  <div>
                    <h2 className="artist-ambassador-section-title">Date {index + 1}</h2>

                    <label className="artist-ambassador-label">
                      Date
                      <div className="mt-2 relative">
                        <input
                          type="date"
                          className="absolute inset-0 h-0 w-0 opacity-0 pointer-events-none"
                          value={slot.date}
                          onChange={event => updateSlot(index, "date", event.target.value)}
                          required={index === 0}
                          min={minSelectableDate}
                          onKeyDown={event => event.preventDefault()}
                          aria-label={`Select date ${index + 1}`}
                          ref={element => {
                            dateInputRefs.current[index] = element;
                          }}
                        />
                        <button
                          type="button"
                          className="artist-ambassador-input"
                          onClick={() => openNativePicker(dateInputRefs.current[index])}
                        >
                          {formatFriendlyDate(slot.date)}
                        </button>
                      </div>
                    </label>

                    <label className="artist-ambassador-label mt-4">
                      Start Time
                      <div className="mt-2 relative">
                        <input
                          type="time"
                          className="absolute inset-0 h-0 w-0 opacity-0 pointer-events-none"
                          value={slot.startTime}
                          onChange={event => updateSlot(index, "startTime", event.target.value)}
                          required={index === 0}
                          step={300}
                          aria-label={`Select start time ${index + 1}`}
                          ref={element => {
                            timeInputRefs.current[index] = element;
                          }}
                        />
                        <button
                          type="button"
                          className="artist-ambassador-input"
                          onClick={() => openNativePicker(timeInputRefs.current[index])}
                        >
                          {formatFriendlyTime(slot.startTime)}
                        </button>
                      </div>
                    </label>
                  </div>

                  <div className="artist-ambassador-slot-card__details">
                    <label className="artist-ambassador-label">
                      Notes for Artist
                      <textarea
                        className="artist-ambassador-textarea"
                        value={slot.notes}
                        onChange={event => updateSlot(index, "notes", event.target.value)}
                        rows={7}
                        placeholder="Include any context that helps with planning, such as class size, accessibility notes, or preferred format."
                      />
                    </label>

                    <button
                      type="button"
                      className="artist-ambassador-button"
                      onClick={() => confirmSlot(index)}
                    >
                      Confirm Date {index + 1}
                    </button>
                  </div>
                </div>
              ) : (
                <button
                  type="button"
                  className={`artist-ambassador-pill ${canAccessSlot(index) ? "artist-ambassador-pill--available" : "artist-ambassador-pill--locked"}`}
                  onClick={() => openSlot(index)}
                  disabled={!canAccessSlot(index)}
                >
                  <span className="artist-ambassador-pill__title">Date {index + 1}</span>
                  {confirmedSlots[index] ? (
                    <span className="artist-ambassador-pill__summary">
                      {formatFriendlyDate(slot.date)} • {formatFriendlyTime(slot.startTime)}
                    </span>
                  ) : canAccessSlot(index) ? (
                    <span className="artist-ambassador-pill__summary">Not confirmed yet</span>
                  ) : null}
                </button>
              )}
            </section>
          ))}

          {confirmedSlots.every(Boolean) ? (
            <ScheduleSection title="Review Confirmed Dates">
              <div className="artist-ambassador-summary-grid">
                {slots.map((slot, index) => (
                  <SlotSummaryCard slot={slot} index={index} key={`review-${index}`} />
                ))}
              </div>
            </ScheduleSection>
          ) : null}

          <div className="artist-ambassador-form__footer">
            <p className="artist-ambassador-section-copy my-0">Submitting as: {userEmail || "No email found"}</p>
            <button
              type="submit"
              className="artist-ambassador-button"
              disabled={!confirmedSlots.every(Boolean) || isSaving}
            >
              {isSaving ? "Saving..." : "Confirm Dates"}
            </button>
          </div>

            {statusMessage ? <p className="artist-ambassador-notice artist-ambassador-notice--success">{statusMessage}</p> : null}
          </form>

          <aside className="artist-ambassador-sidebar" aria-label="Previously proposed dates">
            <ScheduleSection
              title="Previously Proposed Dates"
              subtitle="These are earlier options that were already proposed and rejected."
              className="artist-ambassador-sidebar__panel"
            >
              {historicalDateOptions.length ? (
                <div className="artist-ambassador-rejected-groups">
                  {groupedHistoricalDateOptions.map(yearGroup => (
                    <section key={`historical-year-${yearGroup.year}`} className="artist-ambassador-rejected-year-group">
                      <h3 className="artist-ambassador-rejected-year">{yearGroup.year}</h3>

                      {yearGroup.months.map(monthGroup => (
                        <div key={`historical-${yearGroup.year}-${monthGroup.month}`} className="artist-ambassador-rejected-month-group">
                          <h4 className="artist-ambassador-rejected-month">{monthGroup.month}</h4>

                          <ul className="artist-ambassador-rejected-list">
                            {monthGroup.dayGroups.map((dayGroup, dayGroupIndex) => (
                              <li key={`historical-${yearGroup.year}-${monthGroup.month}-${dayGroupIndex}`} className="artist-ambassador-rejected-item">
                                <p className="artist-ambassador-rejected-item__title">{dayGroup.dayLabel}</p>

                                <ul className="artist-ambassador-rejected-time-list">
                                  {dayGroup.options.map((option, optionIndex) => (
                                    <li key={`historical-time-${yearGroup.year}-${monthGroup.month}-${dayGroupIndex}-${optionIndex}`} className="artist-ambassador-rejected-time-item">
                                      <p className="artist-ambassador-rejected-time-item__time">{formatHistoricalTime(option.dateTime)}</p>
                                      {option.notes?.trim() ? <p className="artist-ambassador-rejected-item__notes">{option.notes}</p> : null}
                                    </li>
                                  ))}
                                </ul>
                              </li>
                            ))}
                          </ul>
                        </div>
                      ))}
                    </section>
                  ))}
                </div>
              ) : (
                <p className="artist-ambassador-section-copy my-0">No earlier rejected date proposals yet.</p>
              )}
            </ScheduleSection>
          </aside>
        </div>
      </div>
    </main>
  );

  if (isArtistUser) {
    if (showArtistProposalTool) {
      return renderDateProposalTool(true);
    }

    if (!isArtistDecisionStatus(bookingStatus)) {
      return (
        <main className="page artist-ambassador-page">
          <div className="artist-ambassador-shell">
            <section className="artist-ambassador-hero">
              <h1 className="artist-ambassador-title">Artist Ambassador Scheduling</h1>
              <p className="artist-ambassador-subtitle">
                {normalizeBookingStatus(bookingStatus) === normalizeBookingStatus(BOOKING_STATUSES.dateSelected)
                  ? "A final date has been selected for this booking."
                  : "Waiting for the educator to review your proposed dates."}
              </p>
            </section>

            <ScheduleSection title="Current Proposed Dates">
              <div className="artist-ambassador-summary-grid">
                {slots
                  .map((slot, index) => ({ slot, index }))
                  .filter(item => normalizeBookingStatus(bookingStatus) !== normalizeBookingStatus(BOOKING_STATUSES.dateSelected) || confirmedArtistDateIndex === null || item.index === confirmedArtistDateIndex)
                  .map(({ slot, index }) => (
                    <SlotSummaryCard
                      key={`artist-waiting-${index}`}
                      slot={slot}
                      index={index}
                      isConfirmed={normalizeBookingStatus(bookingStatus) === normalizeBookingStatus(BOOKING_STATUSES.dateSelected)}
                    />
                  ))}
              </div>
            </ScheduleSection>
          </div>
        </main>
      );
    }

    return (
      <main className="page artist-ambassador-page">
        <div className="artist-ambassador-shell">
          <section className="artist-ambassador-hero">
            <h1 className="artist-ambassador-title">Artist Ambassador Scheduling</h1>
            <p className="artist-ambassador-subtitle">
              Review the educator&apos;s proposed options and confirm the date that works best for your visit.
            </p>
          </section>

          <ScheduleSection
            title="Proposed Dates"
            subtitle="Select one option to confirm with the educator."
            className="artist-ambassador-panel--proposed"
          >
            <div className="artist-ambassador-summary-grid">
              {slots
                .map((slot, index) => ({ slot, index }))
                .filter(item => confirmedArtistDateIndex === null || item.index === confirmedArtistDateIndex)
                .map(({ slot, index }) => {
                  const isDateComplete = Boolean(toBookingDateTime(slot.date, slot.startTime));
                  const isSavingSelectedDate = selectedArtistDateIndex === index;

                  return (
                    <SlotSummaryCard
                      key={`artist-review-${index}`}
                      slot={slot}
                      index={index}
                      isConfirmed={confirmedArtistDateIndex === index}
                      actionLabel={confirmedArtistDateIndex === index ? undefined : "Select this date"}
                      onAction={confirmedArtistDateIndex === index ? undefined : () => handleArtistSelectDate(index)}
                      isActionDisabled={!isDateComplete || selectedArtistDateIndex !== null}
                      isActionSaving={isSavingSelectedDate}
                    />
                  );
                })}
            </div>

            {confirmedArtistDateIndex === null ? (
              <div className="artist-ambassador-panel__action-row">
                <button
                  type="button"
                  className="artist-ambassador-button artist-ambassador-button--secondary"
                  onClick={handleArtistNoneOfTheseDatesWork}
                  disabled={selectedArtistDateIndex !== null}
                >
                  None of these dates work
                </button>
              </div>
            ) : null}

            {artistActionMessage ? <p className="artist-ambassador-notice artist-ambassador-notice--success">{artistActionMessage}</p> : null}
          </ScheduleSection>
        </div>
      </main>
    );
  }

  if (location.pathname.endsWith("/submitted") || isEducatorSubmittedStatus(bookingStatus) || isEducatorDecisionStatus(bookingStatus)) {
    const isDateSelected = normalizeBookingStatus(bookingStatus) === normalizeBookingStatus(BOOKING_STATUSES.dateSelected);
    const isAwaitingEducatorDecision = isEducatorDecisionStatus(bookingStatus);

    return (
      <main className="page artist-ambassador-page">
        <div className="artist-ambassador-shell">
          <section className="artist-ambassador-hero">
            <h1 className="artist-ambassador-title">Artist Ambassador Scheduling</h1>
            <p className="artist-ambassador-subtitle">
              {isAwaitingEducatorDecision
                ? "Please review the artist's proposed dates and select one, or suggest new dates."
                : isDateSelected
                  ? `Thank you, ${userName}. An Artist Ambassador has selected one of your proposed dates.`
                  : `Thank you, ${userName}. Your three date options have been sent to the Artist Ambassador.`}
            </p>
          </section>

          <ScheduleSection title={isDateSelected ? "Confirmed Date" : isAwaitingEducatorDecision ? "Artist Proposed Dates" : "Your Proposed Dates"}>
            <div className="artist-ambassador-summary-grid">
              {slots
                .map((slot, index) => ({ slot, index }))
                .filter(item => !isDateSelected || confirmedArtistDateIndex === null || item.index === confirmedArtistDateIndex)
                .map(({ slot, index }) => (
                  <SlotSummaryCard
                    slot={slot}
                    index={index}
                    isConfirmed={isDateSelected}
                    actionLabel={isAwaitingEducatorDecision ? "Select this date" : undefined}
                    onAction={isAwaitingEducatorDecision ? () => handleEducatorSelectDate(index) : undefined}
                    isActionDisabled={isAwaitingEducatorDecision ? (!toBookingDateTime(slot.date, slot.startTime) || selectedArtistDateIndex !== null) : undefined}
                    isActionSaving={isAwaitingEducatorDecision ? selectedArtistDateIndex === index : undefined}
                    key={`submitted-${index}`}
                  />
                ))}
            </div>

            {isAwaitingEducatorDecision ? (
              <div className="artist-ambassador-panel__action-row">
                <button
                  type="button"
                  className="artist-ambassador-button artist-ambassador-button--secondary"
                  onClick={handleEducatorNoneOfTheseDatesWork}
                  disabled={selectedArtistDateIndex !== null}
                >
                  None of these dates work
                </button>
              </div>
            ) : null}

            {statusMessage ? <p className="artist-ambassador-notice artist-ambassador-notice--success">{statusMessage}</p> : null}
          </ScheduleSection>
        </div>
      </main>
    );
  }


  return renderDateProposalTool(false);
}
